/**
 * M4: Tier state derivation for the Buyer Passport.
 *
 * Page-level Tier 0/1/2 is a PURE render-time derivation from the snapshot.
 * No new persisted state. Changing only an input's verification tier (no new
 * stored field) changes the derived page tier on the next render.
 *
 * Tier mapping:
 *   0 → overall T1, OR no credit score
 *   1 → overall T2 + credit score present
 *   2 → overall T3 + credit score present
 *
 * The headline badge uses the two-of-three overall threshold.
 * Per-row chips still show each input's own tier. The badge makes no
 * all-items claim (no-overclaim invariant, task:headline-tier-no-overclaim).
 */

import type { DashboardSnapshot } from "../../../lib/dashboard";

export type PassportTier = 0 | 1 | 2;

/**
 * The DISPLAYED passport tier — the two-of-three threshold, 1-indexed to
 * match the backend passport contract (t1/t2/t3,
 * see consumer/passport.go) and the ladder / sidebar VERIFIED pill.
 *
 * This is deliberately DISTINCT from `deriveTier` (below):
 *   - `deriveBadgeTier` is the overall verification tier, independent of
 *     credit. Individual inputs can have stronger labels than this badge.
 *   - `deriveTier` answers "are you ready to go to market?" — credit-score-gated
 *     market readiness, used only for the header CTA gating. Never rendered as a
 *     "Tier N" number.
 *
 * Conflating the two is what produced the "sidebar VERIFIED but badge TIER 2"
 * mismatch: the badge showed the 0-indexed, score-gated readiness value.
 */
export type PassportBadgeTier = 1 | 2 | 3;

/**
 * Derive the page-level Tier 0/1/2 from the snapshot.
 *
 * @param snapshot - The current DashboardSnapshot (read-only; no mutation).
 * @returns 0, 1, or 2.
 */
export function deriveTier(snapshot: DashboardSnapshot): PassportTier {
  const badgeTier = deriveBadgeTier(snapshot);

  // Tier 2: bank-verified or lender-approved + a real (non-provisional) credit score.
  if (
    badgeTier === 3 &&
    snapshot.hasCreditScore
  ) {
    return 2;
  }

  // Tier 1: document-backed + a provisional credit score.
  if (badgeTier === 2 && snapshot.hasCreditScore) {
    return 1;
  }

  // Tier 0: self-reported (or no score to pair with document-backed).
  return 0;
}

/**
 * Derive the DISPLAYED badge tier (1/2/3) from distinct present income,
 * savings, and debt inputs, independent of credit-score presence.
 *
 *   1 → self-reported (or no present inputs)
 *   2 → at least two document-level or stronger inputs
 *   3 → at least two connected-source or stronger inputs
 *
 * @param snapshot - The current DashboardSnapshot (read-only; no mutation).
 * @returns 1, 2, or 3.
 */
export function deriveBadgeTier(snapshot: DashboardSnapshot): PassportBadgeTier {
  const qualifying = new Map<string, number>();
  for (const input of snapshot.financialInputs ?? []) {
    if (!input.present || input.freshness_state === "stale") continue;
    if (!(["household_income", "savings", "monthly_debts"] as string[]).includes(input.field)) continue;
    const rank = input.verification_tier === "connected" || input.verification_tier === "pre_approval"
      ? 3 : input.verification_tier === "document" ? 2 : 1;
    qualifying.set(input.field, Math.max(qualifying.get(input.field) ?? 0, rank));
  }
  const ranks = [...qualifying.values()];
  if (ranks.filter((rank) => rank >= 3).length >= 2) return 3;
  if (ranks.filter((rank) => rank >= 2).length >= 2) return 2;
  return 1;
}

/**
 * Human-readable label for the badge tier.
 * Makes no all-items claim; each input keeps its own provenance label.
 */
export function badgeTierLabel(tier: PassportBadgeTier): string {
  switch (tier) {
    case 3:
      return "Verified";
    case 2:
      return "Document";
    case 1:
    default:
      return "Self-reported";
  }
}

/**
 * Human-readable label for the market-readiness tier badge.
 * Describes the overall tier after the distinct financial-input threshold.
 */
export function tierBadgeLabel(tier: PassportTier): string {
  switch (tier) {
    case 2:
      return "Verified";
    case 1:
      return "Document";
    case 0:
    default:
      return "Self-reported";
  }
}
